import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  Grid,
  IconButton,
  Tooltip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Folder as FolderIcon,
  Check as CheckIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { useFolderContext } from '../../context/FolderContext';
import { useAuth } from '../../context/AuthContext';
import { useFolderOperations } from '../../hooks/useFolderOperations';
import { FOLDER_COLORS } from '../../utils/folderUtils';

const ColorOption = styled(Box)(({ theme, selected, color }) => ({
  width: 32,
  height: 32,
  borderRadius: 6,
  backgroundColor: color,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: selected ? `2px solid ${theme.palette.primary.main}` : `2px solid transparent`,
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    transform: 'scale(1.1)',
    boxShadow: theme.shadows[2],
  },
}));

const NewFolderModal = ({ 
  open, 
  onClose, 
  parentFolder = null,
  defaultParentId = null 
}) => {
  const { user } = useAuth();
  const { 
    folderTree, 
    rootFolder, 
    selectedFolderId,
    isRootFolder 
  } = useFolderContext();
  const { 
    createFolder, 
    loading, 
    error, 
    clearError,
    validateFolderName 
  } = useFolderOperations();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: FOLDER_COLORS[0].value,
    parentId: ''
  });
  const [nameError, setNameError] = useState('');

  // Initialize form when modal opens
  useEffect(() => {
    if (open) {
      const initialParentId = defaultParentId || 
                             parentFolder?.id || 
                             selectedFolderId || 
                             rootFolder?.id || 
                             '';

      setFormData({
        name: '',
        description: '',
        color: FOLDER_COLORS[0].value,
        parentId: initialParentId
      });
      setNameError('');
      clearError();
    }
  }, [open, defaultParentId, parentFolder, selectedFolderId, rootFolder, clearError]);

  // Get all available parent folders (flatten tree structure)
  const getAvailableParents = (folders = folderTree, result = []) => {
    for (const folder of folders) {
      result.push(folder);
      if (folder.children && folder.children.length > 0) {
        getAvailableParents(folder.children, result);
      }
    }
    return result;
  };

  const availableParents = getAvailableParents();

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear name error when user starts typing
    if (field === 'name' && nameError) {
      setNameError('');
    }
  };

  const handleColorSelect = (color) => {
    setFormData(prev => ({
      ...prev,
      color
    }));
  };

  const handleSubmit = async () => {
    try {
      // Validate name
      const nameValidation = validateFolderName(formData.name);
      if (!nameValidation.isValid) {
        setNameError(nameValidation.error);
        return;
      }

      // Create folder
      await createFolder({
        name: formData.name.trim(),
        description: formData.description.trim(),
        color: formData.color,
        tags: []
      }, formData.parentId || null);

      // Close modal on success
      onClose();
    } catch (err) {
      // Error is handled by the hook
      console.error('Failed to create folder:', err);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      color: FOLDER_COLORS[0].value,
      parentId: ''
    });
    setNameError('');
    clearError();
    onClose();
  };

  const canSubmit = formData.name.trim().length > 0 && !loading;

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center">
          <FolderIcon sx={{ mr: 1, color: 'primary.main' }} />
          Create New Folder
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Folder Name */}
        <TextField
          autoFocus
          margin="dense"
          label="Folder Name"
          fullWidth
          variant="outlined"
          value={formData.name}
          onChange={(e) => handleInputChange('name', e.target.value)}
          error={Boolean(nameError)}
          helperText={nameError}
          sx={{ mb: 2 }}
          onKeyPress={(e) => {
            if (e.key === 'Enter' && canSubmit) {
              handleSubmit();
            }
          }}
        />

        {/* Parent Folder Selection */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Parent Folder</InputLabel>
          <Select
            value={formData.parentId}
            label="Parent Folder"
            onChange={(e) => handleInputChange('parentId', e.target.value)}
          >
            {availableParents.map((folder) => (
              <MenuItem key={folder.id} value={folder.id}>
                <Box display="flex" alignItems="center">
                  {isRootFolder(folder.id) ? (
                    <PersonIcon sx={{ mr: 1, fontSize: 18, color: 'primary.main' }} />
                  ) : (
                    <FolderIcon sx={{ mr: 1, fontSize: 18 }} />
                  )}
                  {folder.name}
                  {isRootFolder(folder.id) && (
                    <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                      (Workspace)
                    </Typography>
                  )}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Description */}
        <TextField
          margin="dense"
          label="Description (Optional)"
          fullWidth
          multiline
          rows={2}
          variant="outlined"
          value={formData.description}
          onChange={(e) => handleInputChange('description', e.target.value)}
          placeholder="Add a description for your folder..."
          sx={{ mb: 3 }}
        />

        {/* Color Selection */}
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Folder Color
          </Typography>
          <Grid container spacing={1}>
            {FOLDER_COLORS.map((colorOption) => (
              <Grid item key={colorOption.value}>
                <Tooltip title={colorOption.name}>
                  <ColorOption
                    color={colorOption.value}
                    selected={formData.color === colorOption.value}
                    onClick={() => handleColorSelect(colorOption.value)}
                  >
                    {formData.color === colorOption.value && (
                      <CheckIcon 
                        sx={{ 
                          fontSize: 16, 
                          color: colorOption.value === '#ffeb3b' ? '#333' : 'white' 
                        }} 
                      />
                    )}
                  </ColorOption>
                </Tooltip>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Preview */}
        <Box 
          sx={{ 
            mt: 3, 
            p: 2, 
            backgroundColor: 'background.default', 
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
            Preview:
          </Typography>
          <Box display="flex" alignItems="center">
            <FolderIcon sx={{ color: formData.color, mr: 1 }} />
            <Typography variant="body2">
              {formData.name || 'New Folder'}
            </Typography>
          </Box>
          {formData.description && (
            <Typography variant="caption" sx={{ mt: 0.5, color: 'text.secondary' }}>
              {formData.description}
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained"
          disabled={!canSubmit}
          sx={{ minWidth: 100 }}
        >
          {loading ? 'Creating...' : 'Create Folder'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewFolderModal;
